import { useState } from 'react'
import { motion, useAnimation } from 'framer-motion'
import { cn } from '@/lib/utils'
import { Reel } from './Reel'
import { type SlotSymbol, PAYLINES, PAYLINE_COLORS, type WinResult } from '../utils/slotsLogic'

interface SlotMachineProps {
  visibleSymbols: SlotSymbol[][]
  isSpinning: boolean
  wins: WinResult[]
  activePaylines: number
  lastWin: number
  onPullLever?: () => void
  canSpin: boolean
}

export function SlotMachine({
  visibleSymbols,
  isSpinning,
  wins,
  activePaylines,
  lastWin,
  onPullLever,
  canSpin,
}: SlotMachineProps) {
  const [leverPulled, setLeverPulled] = useState(false)
  const leverControls = useAnimation()

  const getWinningRowsForReel = (reelIndex: number): number[] => {
    const winningRows = new Set<number>()
    for (const win of wins) {
      for (const [reel, row] of win.positions) {
        if (reel === reelIndex) {
          winningRows.add(row)
        }
      }
    }
    return Array.from(winningRows)
  }

  const handleLeverPull = async () => {
    if (!canSpin || isSpinning || leverPulled) return

    setLeverPulled(true)

    // Animate lever down
    await leverControls.start({
      rotate: 35,
      transition: { duration: 0.2, ease: 'easeOut' },
    })

    // Trigger spin
    onPullLever?.()

    // Animate lever back up
    await leverControls.start({
      rotate: 0,
      transition: { duration: 0.5, ease: 'easeOut', delay: 0.2 },
    })

    setLeverPulled(false)
  }

  return (
    <div className="relative flex items-center gap-0">
      {/* Main Machine */}
      <div className="relative">
        {/* Outer Machine Frame - Casino Style */}
        <div
          className={cn(
            'relative rounded-3xl overflow-hidden',
            'bg-gradient-to-b from-zinc-800 via-zinc-900 to-black',
            'shadow-[0_0_60px_rgba(0,0,0,0.8),inset_0_2px_0_rgba(255,255,255,0.1)]',
            'border-4 border-zinc-700'
          )}
        >
          {/* Top Chrome Strip with LED Lights */}
          <div className="relative h-20 bg-gradient-to-b from-zinc-600 via-zinc-700 to-zinc-800 border-b-4 border-zinc-900">
            {/* LED Light Bar */}
            <div className="absolute inset-x-0 top-3 flex justify-center gap-3 px-6">
              {Array.from({ length: 12 }).map((_, i) => (
                <motion.div
                  key={i}
                  className="w-3 h-3 rounded-full"
                  style={{
                    backgroundColor: i % 2 === 0 ? '#ef4444' : '#eab308',
                  }}
                  animate={{
                    opacity: [0.4, 1, 0.4],
                    boxShadow: [
                      `0 0 4px ${i % 2 === 0 ? '#ef4444' : '#eab308'}`,
                      `0 0 15px ${i % 2 === 0 ? '#ef4444' : '#eab308'}`,
                      `0 0 4px ${i % 2 === 0 ? '#ef4444' : '#eab308'}`,
                    ],
                  }}
                  transition={{
                    duration: 0.8,
                    repeat: Infinity,
                    delay: i * 0.1,
                  }}
                />
              ))}
            </div>

            {/* Title Plate */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2">
              <div className="relative px-10 py-2 bg-gradient-to-b from-casino-gold via-yellow-500 to-yellow-600 rounded-lg shadow-lg">
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-lg" />
                <span className="relative font-cinzel font-bold text-lg tracking-[0.2em] text-zinc-900 drop-shadow-sm">
                  COSA NOSTRA
                </span>
              </div>
            </div>
          </div>

          {/* Main Reel Area */}
          <div className="relative p-6 md:p-8">
            {/* Inner Frame with Metallic Border */}
            <div className="relative p-1.5 rounded-2xl bg-gradient-to-b from-zinc-500 via-zinc-600 to-zinc-700 shadow-[inset_0_2px_4px_rgba(0,0,0,0.5)]">
              <div className="relative p-4 rounded-xl bg-gradient-to-b from-zinc-900 via-black to-zinc-900">
                {/* Reels Container */}
                <div className="flex gap-2 md:gap-3 justify-center">
                  {visibleSymbols.map((reelSymbols, reelIndex) => (
                    <Reel
                      key={reelIndex}
                      symbols={reelSymbols}
                      isSpinning={isSpinning}
                      reelIndex={reelIndex}
                      winningRows={getWinningRowsForReel(reelIndex)}
                    />
                  ))}
                </div>

                {/* Payline Indicators - Left */}
                <div className="absolute left-1 top-1/2 -translate-y-1/2 flex flex-col gap-1.5">
                  {Array.from({ length: Math.min(activePaylines, 5) }).map((_, i) => (
                    <motion.div
                      key={i}
                      className={cn(
                        'w-5 h-5 rounded-full border-2 flex items-center justify-center',
                        'text-[9px] font-bold',
                        'shadow-lg'
                      )}
                      style={{
                        backgroundColor: PAYLINE_COLORS[i],
                        borderColor: 'rgba(255,255,255,0.3)',
                      }}
                      animate={
                        wins.some((w) => w.paylineIndex === i)
                          ? {
                              scale: [1, 1.4, 1],
                              boxShadow: [
                                `0 0 5px ${PAYLINE_COLORS[i]}`,
                                `0 0 20px ${PAYLINE_COLORS[i]}`,
                                `0 0 5px ${PAYLINE_COLORS[i]}`,
                              ],
                            }
                          : {}
                      }
                      transition={{ duration: 0.5, repeat: Infinity }}
                    >
                      {i + 1}
                    </motion.div>
                  ))}
                </div>

                {/* Payline Indicators - Right */}
                <div className="absolute right-1 top-1/2 -translate-y-1/2 flex flex-col gap-1.5">
                  {Array.from({ length: Math.min(activePaylines, 5) }).map((_, i) => {
                    const index = i + 5
                    if (index >= activePaylines) return null
                    return (
                      <motion.div
                        key={index}
                        className={cn(
                          'w-5 h-5 rounded-full border-2 flex items-center justify-center',
                          'text-[9px] font-bold',
                          'shadow-lg'
                        )}
                        style={{
                          backgroundColor: PAYLINE_COLORS[index],
                          borderColor: 'rgba(255,255,255,0.3)',
                        }}
                        animate={
                          wins.some((w) => w.paylineIndex === index)
                            ? {
                                scale: [1, 1.4, 1],
                                boxShadow: [
                                  `0 0 5px ${PAYLINE_COLORS[index]}`,
                                  `0 0 20px ${PAYLINE_COLORS[index]}`,
                                  `0 0 5px ${PAYLINE_COLORS[index]}`,
                                ],
                              }
                            : {}
                        }
                        transition={{ duration: 0.5, repeat: Infinity }}
                      >
                        {index + 1}
                      </motion.div>
                    )
                  })}
                </div>

                {/* Win Lines Overlay */}
                {wins.length > 0 && !isSpinning && (
                  <svg
                    className="absolute inset-4 pointer-events-none"
                    viewBox="0 0 400 200"
                    preserveAspectRatio="none"
                  >
                    {wins.map((win, winIndex) => {
                      const payline = PAYLINES[win.paylineIndex]
                      const color = PAYLINE_COLORS[win.paylineIndex]
                      const points = payline.slice(0, win.count).map(([reel, row]) => {
                        const x = 40 + reel * 80
                        const y = 33 + row * 67
                        return `${x},${y}`
                      })

                      return (
                        <motion.polyline
                          key={winIndex}
                          points={points.join(' ')}
                          fill="none"
                          stroke={color}
                          strokeWidth="4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          filter="url(#glow)"
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={{ pathLength: 1, opacity: 1 }}
                          transition={{ duration: 0.4, delay: winIndex * 0.15 }}
                        />
                      )
                    })}
                    <defs>
                      <filter id="glow">
                        <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                        <feMerge>
                          <feMergeNode in="coloredBlur" />
                          <feMergeNode in="SourceGraphic" />
                        </feMerge>
                      </filter>
                    </defs>
                  </svg>
                )}
              </div>
            </div>

            {/* Win Display - Only one time */}
            {lastWin > 0 && !isSpinning && (
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="absolute inset-0 flex items-center justify-center pointer-events-none"
              >
                <div
                  className={cn(
                    'px-10 py-5 rounded-2xl',
                    'bg-gradient-to-r from-casino-gold via-yellow-400 to-casino-gold',
                    'shadow-[0_0_40px_rgba(212,175,55,0.8)]',
                    'border-4 border-yellow-300'
                  )}
                >
                  <div className="text-center">
                    <div className="text-sm font-bold text-zinc-900 uppercase tracking-wider">
                      GANASTE
                    </div>
                    <div className="text-4xl md:text-5xl font-bold text-zinc-900">
                      ${lastWin.toLocaleString()}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {/* Bottom Chrome Strip */}
          <div className="h-10 bg-gradient-to-b from-zinc-700 via-zinc-800 to-zinc-900 border-t border-zinc-600">
            <div className="h-full flex items-center justify-center gap-6">
              {/* Decorative Screws */}
              {[0, 1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="w-4 h-4 rounded-full bg-gradient-to-br from-zinc-400 to-zinc-600 shadow-inner"
                />
              ))}
            </div>
          </div>
        </div>

        {/* Side Decorations - Left */}
        <div className="absolute -left-4 top-1/2 -translate-y-1/2 flex flex-col gap-4">
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-red-700 border-2 border-zinc-600"
              animate={{
                boxShadow: [
                  '0 0 5px rgba(239,68,68,0.5)',
                  '0 0 20px rgba(239,68,68,0.8)',
                  '0 0 5px rgba(239,68,68,0.5)',
                ],
              }}
              transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.3 }}
            />
          ))}
        </div>
      </div>

      {/* LEVER - Premium Design */}
      <div className="relative -ml-2">
        {/* Lever Assembly */}
        <div className="relative flex flex-col items-center">
          {/* Top Mount - Chrome Cap */}
          <div className="relative z-10">
            <div className="w-12 h-12 rounded-full bg-gradient-to-b from-zinc-400 via-zinc-500 to-zinc-600 border-4 border-zinc-700 shadow-lg flex items-center justify-center">
              <div className="w-6 h-6 rounded-full bg-gradient-to-b from-zinc-600 to-zinc-800 border-2 border-zinc-500" />
            </div>
          </div>

          {/* Lever Arm - Pivots from top */}
          <motion.div
            animate={leverControls}
            style={{ originY: 0, originX: 0.5 }}
            className="relative cursor-pointer"
            onClick={handleLeverPull}
          >
            {/* Arm */}
            <div
              className={cn(
                'w-4 h-44 rounded-full',
                'bg-gradient-to-b from-amber-600 via-amber-500 to-amber-600',
                'border-2 border-amber-700',
                'shadow-[2px_4px_10px_rgba(0,0,0,0.5)]',
                !canSpin || isSpinning ? 'opacity-50' : ''
              )}
            >
              {/* Chrome bands */}
              <div className="absolute top-8 left-0 right-0 h-2 bg-gradient-to-b from-zinc-300 to-zinc-500 rounded-full" />
              <div className="absolute top-20 left-0 right-0 h-2 bg-gradient-to-b from-zinc-300 to-zinc-500 rounded-full" />
              <div className="absolute top-32 left-0 right-0 h-2 bg-gradient-to-b from-zinc-300 to-zinc-500 rounded-full" />
            </div>

            {/* Handle Ball */}
            <motion.div
              className={cn(
                'absolute -bottom-10 left-1/2 -translate-x-1/2',
                'w-16 h-16 rounded-full',
                'bg-gradient-to-br from-red-400 via-red-600 to-red-800',
                'border-4 border-red-300',
                'shadow-[0_8px_20px_rgba(0,0,0,0.6),inset_0_-4px_10px_rgba(0,0,0,0.3)]',
                'flex items-center justify-center',
                !canSpin || isSpinning ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'
              )}
              whileHover={canSpin && !isSpinning ? {
                scale: 1.1,
                boxShadow: '0 0 30px rgba(239,68,68,0.8), 0 8px 20px rgba(0,0,0,0.6)',
              } : undefined}
              whileTap={canSpin && !isSpinning ? { scale: 0.95 } : undefined}
            >
              {/* Glossy shine */}
              <div className="absolute top-1 left-2 w-6 h-6 rounded-full bg-gradient-to-br from-white/60 to-transparent" />

              {/* Center emblem */}
              <div className="w-8 h-8 rounded-full bg-gradient-to-b from-red-800 to-red-900 border-2 border-red-600 flex items-center justify-center">
                <span className="text-yellow-400 text-lg font-bold">$</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Base Plate */}
          <div className="absolute bottom-0 translate-y-16">
            <div className="w-20 h-6 bg-gradient-to-b from-zinc-600 to-zinc-800 rounded-lg border-2 border-zinc-500 shadow-lg" />
          </div>
        </div>

        {/* Instruction */}
        {canSpin && !isSpinning && (
          <motion.div
            className="absolute -bottom-4 left-1/2 -translate-x-1/2 whitespace-nowrap"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <span className="text-xs text-casino-gold font-semibold">
              ↓ TIRÁ ↓
            </span>
          </motion.div>
        )}
      </div>
    </div>
  )
}
